import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/Cordwood';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";
import {TimelineReportRequest, TimelineReportResult} from "../types/models/LogGroups";
import {CordwoodGroupQueryRequest, CordwoodGroupQueryResult} from "../types/models/CordwoodGroups";

export const CORDWOOD_API_REDUCER_KEY = 'cordwoods';
export const CORDWOOD_PATH = 'cordwoods';

export const cordwoodApi = createApi({
    reducerPath: CORDWOOD_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['Cordwood', 'Cordwoods'],
    endpoints: (builder) => ({
        getCordwoodList: builder.query<PaginationResult<Entity.Cordwood>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ CORDWOOD_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['Cordwoods'],
        }),
        getCordwood: builder.query<Entity.Cordwood, Entity.PrimaryCordwood>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = cordwoodNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ CORDWOOD_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['Cordwood'],
        }),
        getCordwoodMultiple: builder.query<Entity.Cordwood, Entity.PrimaryCordwood[]>({
            query: (ids) => {
                return ({
                    url: `/${ CORDWOOD_PATH }/ids${Entity.PrimaryCordwood.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['Cordwoods'],
        }),
        getCordwoodPresentation: builder.query<PaginationResult<Entity.Cordwood>, Entity.PresentationCordwoodRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ CORDWOOD_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['Cordwoods'],
        }),
        getCordwoodAdvancedPresentation: builder.query<PaginationResult<Entity.Cordwood>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ CORDWOOD_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['Cordwoods'],
        }),
        
        insertCordwood: builder.mutation<Entity.Cordwood, Entity.CreateCordwood>({
            query: (toInsert) => {
                return ({
                    url: `/${ CORDWOOD_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    cordwoodApi.util.upsertQueryData('getCordwood', Entity.PrimaryCordwood.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Cordwoods'],
        }),
        insertMultipleCordwood: builder.mutation<Entity.Cordwood[], Entity.CreateCordwood[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ CORDWOOD_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            cordwoodApi.util.upsertQueryData('getCordwood', Entity.PrimaryCordwood.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['Cordwoods'],
        }),
        updateCordwood: builder.mutation<Entity.Cordwood, Entity.UpdateEntityCordwood>({
            query: (toUpdate) => {
                return ({
                    url: `/${ CORDWOOD_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['Cordwoods'],
        }),
        updateMultipleCordwood: builder.mutation<Entity.Cordwood[], Entity.UpdateEntityCordwood[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ CORDWOOD_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['Cordwoods'],
        }),
        deleteCordwood: builder.mutation<Entity.Cordwood, Entity.Cordwood>({
            query: (toDelete) => {
                return ({
                    url: `/${ CORDWOOD_PATH }/${Entity.PrimaryCordwood.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Cordwoods'],
        }),
        deleteMultipleCordwood: builder.mutation<Entity.Cordwood[], Entity.PrimaryCordwood[]>({
            query: (ids) => {
                return ({
                    url: `/${ CORDWOOD_PATH }/batch${Entity.PrimaryCordwood.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Cordwoods'],
        }),
        assignShipmentCordwood: builder.mutation<Entity.Cordwood, Entity.AssignShipmentCordwood>({
            query: (assign) => {
                return ({
                    url: `/${ CORDWOOD_PATH }/shipment`,
                    method: 'POST',
                    data: assign,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    cordwoodApi.util.upsertQueryData('getCordwood', Entity.PrimaryCordwood.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Cordwoods'],
        }),
        getGroupWithShipments: builder.query<PaginationResult<CordwoodGroupQueryResult>, CordwoodGroupQueryRequest>({
            query: (request) => {

                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ CORDWOOD_PATH }/groupBy/typesWithShipments?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: (_result, _error, request) => [{ type: 'Cordwood', id: 'Group' }]
        }),
        getGroup: builder.query<Array<CordwoodGroupQueryResult>, CordwoodGroupQueryRequest>({
            query: (request) => {

                function removeNullProperties(obj: Record<string, any>): Record<string, any> {
                    return Object.fromEntries(
                        Object.entries(obj)
                            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
                    );
                }

                const queryParams: Record<string, any> = removeNullProperties(request);
                return ({
                    url: `/${ CORDWOOD_PATH }/groupBy/types`,
                    method: 'POST',
                    data: queryParams,
                })
            },
            providesTags: (_result, _error, request) => [{ type: 'Cordwood', id: 'Group' }]
        }),
        getCordwoodTimelineShipped: builder.query<TimelineReportResult[], TimelineReportRequest>({
            query: (request) => ({
                url: `/${CORDWOOD_PATH}/report/timeline/shipped`,
                method: 'POST',
                data: request,
            }),
            providesTags: (_result, _error, request) => [{ type: 'Cordwood', id: 'TimelineShipped' }]
        }),
        getTotal: builder.query<Entity.CordwoodTotalQueryResult[], Entity.CordwoodTotalQueryRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = request;
                return ({
                    url: `/${CORDWOOD_PATH}/groupBy/total?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: (_result, _error, _request) => [{ type: 'Cordwood', id: 'Total' }]
        }),
    }),
})

export const [cordwoodNormalizer, cordwoodNormalizerMiddleware] = createNormalizationMiddleware(CORDWOOD_API_REDUCER_KEY, cordwoodApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetCordwoodListQuery,
    useGetCordwoodMultipleQuery,
    useGetCordwoodPresentationQuery,
    useGetCordwoodQuery,
    useInsertCordwoodMutation,
    useInsertMultipleCordwoodMutation,
    useUpdateCordwoodMutation,
    useUpdateMultipleCordwoodMutation,
    useDeleteCordwoodMutation,
    useDeleteMultipleCordwoodMutation,
    useGetCordwoodAdvancedPresentationQuery,
    useGetGroupWithShipmentsQuery,
    useGetGroupQuery,
    useAssignShipmentCordwoodMutation,
    useGetCordwoodTimelineShippedQuery,
    useGetTotalQuery,
    
} = cordwoodApi
